import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { MessageService } from 'primeng/api';
import { UserService } from '../../user/services/user.service';
import * as DashboardActions from '../../store/tenant/dashboard.action';
import { IAppState } from '../../store/interface/app.state.interface';
import { Store } from '@ngrx/store';
import { TenantService } from '../../tenant/services/tenant.service';
import * as UserActions from '../../store/user/user.actions';
import { IUser } from '../../user/interfaces/user.interface';
import { UserTypes } from '../../shared/enum/user-types.enum';
import { environment } from '../../../environments/environment';

@Injectable()
export class AuthGuard implements CanActivate {
    constructor(
        private router: Router,
        private userService: UserService,
        private tenantService: TenantService,
        private store: Store<IAppState>,
        private messageService: MessageService,
    ) {}
    canActivate(): Promise<boolean | UrlTree> {
        const token = localStorage.getItem('auth_token');

        if (!token) {
            this.messageService.add({
                severity: 'info',
                summary: 'Session Expired',
                detail: 'Your session has expired. Please sign in again.',
                closable: false,
            });
            return Promise.resolve(this.router.parseUrl('/auth/login'));
        }

        return Promise.all([
            this.userService.fetchLoggedInUser(),

            this.tenantService.fetchTenantStatsCount().then((data) => {
                this.store.dispatch(
                    DashboardActions.setTenantStatsCount({
                        counts: data,
                    }),
                );
                return data;
            }),
        ])
            .then(() =>
                this.hasNoStore() && !environment.production
                    ? this.router.parseUrl('/onboarding/shop')
                    : true,
            )
            .catch(() => {
                localStorage.removeItem('auth_token');
                this.store.dispatch(UserActions.setLoggedInUser({ user: null }));
                return this.router.parseUrl('/auth/login');
            });
    }

    /*=====================================
      Whether this account is in no store at all.

      An account can hold no membership: its only store was deleted, a signup
      was abandoned inside the wizard, or somebody was removed from the last
      store they worked in. There is no empty-panel screen - every screen here
      needs a store to mean anything - so the answer is to go and make one.

      A PLATFORM account also has no store while it sits in Seldit context,
      and must not be thrown into the shop wizard. The user type is what
      separates the two, the same way SystemUserGuard reads it.
      Reads the account off the store rather than off fetchLoggedInUser, which
      returns nothing by design - the store is where the account lives, and one
      guard's convenience is not a reason to widen a service every screen uses.
      The select is synchronous, so subscribing and releasing it immediately
      reads the current value without leaving a subscription behind.
      @return whether the panel has nothing to show this account
    ======================================*/
    private hasNoStore(): boolean {
        let account: IUser | null = null;
        this.store
            .select((state) => state.user.loggedInUser)
            .subscribe((value) => (account = value))
            .unsubscribe();

        const user = account as IUser | null;
        if (!user || user.userType?.key === UserTypes.TT_USER) {
            return false;
        }

        return !user.tenant?.id;
    }
}
